import React, { useEffect, useMemo, useState, useRef } from "react";
import { Form, Button, Modal } from "react-bootstrap";
import { SearchResponse, TransportItem, TransportModeCode } from "../../types";
import { getTransportIcon } from "../../functions/ItineraryUtils";
import { FaArrowDownWideShort, FaSliders } from "react-icons/fa6";
import LoadingMessage from "../LoadingMessage";
import TransportDetailModal from "./TransportDetailModal";

const transportModes = ["", "flight", "train", "ferry", "bus", "carpool"];

// type Result = {
//   id: string;
//   A: number; // prix
//   C: string; // devise
//   cpd: Carrier;
//   modes: TransportMode[];
//   du: number;
//   ch: number;
//   nf: string;
//   nt: string;
//   dep: string;
//   arr: string;
//   segments: Segment[];
// };

type Props = {
  responses: SearchResponse[];
  renderResult: (result: TransportItem) => React.ReactNode;
  completed: number;
  overview?: boolean;
  results_length?: number;
  transport_mode_str?: string | null;
};

const weights = {
  PRICE: 1.5,
  DURATION: 1,
};
// const computePerformance = (r: TransportItem) => r.A / r.du;

const TransportSearchResults: React.FC<Props> = ({
  responses,
  renderResult,
  completed,
  overview = false,
  results_length = null,
  transport_mode_str = null,
}) => {
  // 🟩 Conversion du mode string -> code numérique
  const numModeId: TransportModeCode | null = useMemo(() => {
    if (!transport_mode_str) return null;
    const index = transportModes.indexOf(transport_mode_str);
    return index > 0 ? (index as TransportModeCode) : null;
  }, [transport_mode_str]);

  const agentParams = useRef<Record<number, any>>({});

  const allResults = useMemo(() => {
    if (!responses || responses.length === 0) return [];

    // const merged: TransportItem[] = responses.flatMap((r) => {
    //   const { outbound = [], inbound = [] } = r.results || {};
    //   return [...outbound, ...inbound];
    // });
    const merged: TransportItem[] = responses.flatMap((r) => {

      if(r.agent_id && !(r.agent_id in agentParams.current)) {
        agentParams.current[r.agent_id] = r.params;
      }

      return r.results.outbound ? r.results.outbound.map(
        (item: TransportItem) => (
          {...item,
            agent_id: r.agent_id
          }
        )
      ) : [];
    });

    return merged;
  }, [responses]);


  const [sortBy, setSortBy] = useState<
    "price" | "duration" | "departure" | "stops" | "performance"
  >("performance");
  const [filterModes, setFilterModes] = useState<TransportModeCode[]>([]);
  const [sortMode, setSortMode] = useState<TransportModeCode | null>(numModeId);
  const [filterCarriers, setFilterCarriers] = useState<string[]>([]);
  const [filterDepartures, setFilterDepartures] = useState<string[]>([]);
  const [filterArrivals, setFilterArrivals] = useState<string[]>([]);
  const [indexPrice, setIndexPrice] = useState<number | null>(null);
  const [indexDuration, setIndexDuration] = useState<number | null>(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<TransportItem | null>(
    null
  );
  const [selectedAgentParams, setSelectedAgentParams] = useState<any | null>(
    null
  );
  const [showDetailModal, setShowDetailModal] = useState(false);

  const computePerformance = (r: TransportItem) => {
    if (indexPrice === null || indexDuration === null) {
      return 1e4;
    }
    const priceIndex = (r.A * 100) / indexPrice;
    const durationIndex = (r.du * 100) / indexDuration;

    return (
      (priceIndex * weights.PRICE + durationIndex * weights.DURATION) /
      (weights.PRICE + weights.DURATION)
    );
  };

  const toggleInArray = <T,>(array: T[], value: T): T[] =>
    array.includes(value)
      ? array.filter((v) => v !== value)
      : [...array, value];

  // Unique filters
  const uniqueModes = useMemo(
    () => [...new Set(allResults.flatMap((r) => r.modes))],
    [allResults]
  );
  const uniqueCarriers = useMemo(
    () => [...new Set(allResults.map((r) => r.cpd.n))],
    [allResults]
  );
  const uniqueDepartures = useMemo(
    () => [...new Set(allResults.map((r) => r.nf))],
    [allResults]
  );
  const uniqueArrivals = useMemo(
    () => [...new Set(allResults.map((r) => r.nt))],
    [allResults]
  );

  // Filtered results
  const filteredResults = useMemo(() => {
    return allResults
      .filter(
        (r) =>
          filterModes.length === 0 ||
          r.modes.some((m) => filterModes.includes(m as TransportModeCode))
      )
      .filter(
        (r) => filterCarriers.length === 0 || filterCarriers.includes(r.cpd.n)
      )
      .filter(
        (r) => filterDepartures.length === 0 || filterDepartures.includes(r.nf)
      )
      .filter(
        (r) => filterArrivals.length === 0 || filterArrivals.includes(r.nt)
      );
  }, [allResults, filterModes, filterCarriers, filterDepartures, filterArrivals]);

  const sortedResults = useMemo(() => {
    const sorted = [...filteredResults];

    sorted.sort((a, b) => {
      // 1️⃣ --- TRI PAR MODE DE TRANSPORT ---
      if (sortMode) {
        const aIsPreferred = a.modes ? a.modes.includes(sortMode) : false;
        const bIsPreferred = b.modes ? b.modes.includes(sortMode) : false;

        // Si un seul des deux correspond au mode choisi, il passe en premier
        if (aIsPreferred && !bIsPreferred) return -1;
        if (!aIsPreferred && bIsPreferred) return 1;
        // Sinon, continuer le tri secondaire
      }

      // 2️⃣ --- TRI SECONDAIRE PAR CRITÈRE ---

      switch (sortBy) {
        case "price":
          return a.A - b.A;
        case "duration":
          return a.du - b.du;
        case "departure":
          return new Date(a.dep).getTime() - new Date(b.dep).getTime();
        case "stops":
          return a.ch - b.ch;
        case "performance":
          return computePerformance(a) - computePerformance(b);
        default:
          return 0;
      }
    });

    return results_length ? sorted.slice(0, results_length) : sorted;
  }, [filteredResults, sortBy, results_length, sortMode]);

  const handleSelection = (offer: TransportItem) => {

    setSelectedOffer(offer);
    if (agentParams && offer.agent_id !== undefined && offer.agent_id in agentParams.current ) {
      setSelectedAgentParams(
        agentParams.current[offer.agent_id]
      );

    }

    setShowDetailModal(true);
  };

  const closeSelectedOfferModal = () => {
    setSelectedOffer(null);
    setShowDetailModal(false);
  };

  useEffect(() => {
    setFilterCarriers(uniqueCarriers);
  }, [uniqueCarriers]);

  useEffect(() => {
    setFilterDepartures(uniqueDepartures);
  }, [uniqueDepartures]);

  useEffect(() => {
    setFilterArrivals(uniqueArrivals);
  }, [uniqueArrivals]);

  useEffect(() => {
    setFilterArrivals(uniqueArrivals);
  }, [uniqueArrivals]);

  // 🟦 Si la prop change dynamiquement (nouveau mode transmis)
  useEffect(() => {
    if (numModeId) {
      setSortMode(numModeId);
    }
  }, [numModeId]);

  useEffect(() => {
    if (allResults && allResults.length > 0) {
      setIndexPrice(allResults[0].A);
      setIndexDuration(allResults[0].du);
    }
  }, [allResults]);

  return (
    <div
      className="container px-0 position-relative flex-grow-1 h-100"
      style={{ minHeight: 0 }}
    >
      {!overview && (
        <div className="mt-3 sticky-top ">
          <Form className="mb-4 bg-white py-2">
            <div className="d-flex align-items-center">
              {allResults.length > 0 && (
                <div className="d-flex flex-column me-3">{`${sortedResults.length} / ${allResults.length}`}</div>
              )}

              <div className="d-flex flex-column me-3">
                <Button
                  size="sm"
                  variant="outline-info rounded-4 px-3"
                  className="d-flex align-items-center"
                  onClick={() => setShowFilterModal(true)}
                >
                  <FaSliders className="me-2" />
                  <span>Filtres</span>
                </Button>
              </div>

              {/* Tri principal */}
              <div className="d-flex flex-column me-3">
                <Form.Select
                  size="sm"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="rounded-4 border-info text-info px-3"
                >
                  <option value="performance">Performance</option>
                  <option value="price">Prix</option>
                  <option value="duration">Durée</option>
                  <option value="departure">Heure de départ</option>
                  <option value="stops">Nombre d’escales</option>
                </Form.Select>
              </div>

              {/* Modes de transport */}
              {uniqueModes.length > 1 && (
                <div className="d-flex flex-column">
                  <div className="btn-group" role="group">
                    {uniqueModes.map((mode) => (
                      <Button
                        key={mode}
                        variant={sortMode === mode ? "info" : "outline-info"}
                        // onClick={() =>
                        //   setFilterModes(
                        //     toggleInArray(
                        //       filterModes,
                        //       mode as TransportModeCode
                        //     )
                        //   )
                        // }
                        onClick={() => setSortMode(mode as TransportModeCode)}
                      >
                        {getTransportIcon(mode)}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Top 3 checkbox */}
              {/* <div className="d-flex flex-column">
            <Form.Check
              type="checkbox"
              label="Top 3 seulement"
              checked={topOnly}
              onChange={(e) => setTopOnly(e.target.checked)}
            />
          </div> */}
            </div>
          </Form>
        </div>
      )}

      <div
        className="results-list d-flex flex-column h-100"
        style={{ minHeight: 0 }}
      >
        <div className="overflow-auto flex-grow-1">
          {completed < 1 && <LoadingMessage />}
          {completed == 1 && sortedResults.length === 0 && (
            <p>Aucun résultat trouvé.</p>
          )}
          {sortedResults.map((result, index) => (
            <div
              key={index}
              className=""
              onClick={() => handleSelection(result)}
            >
              {renderResult(result)}
            </div>
          ))}
        </div>
      </div>

      <Modal
        show={showFilterModal}
        onHide={() => setShowFilterModal(false)}
        size="lg"
        centered
        scrollable={true}
      >
        <Modal.Header closeButton>
          <Modal.Title>Filtres</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {/* Compagnies */}
            <Form.Group className="mb-3">
              <Form.Label className="h5 fw-bold">Compagnies</Form.Label>
              {uniqueCarriers.map((carrier, i) => {
                const id = `carrier-${i}`;
                return (
                  <Form.Check
                    key={id}
                    type="checkbox"
                    id={id}
                    label={carrier}
                    checked={filterCarriers.includes(carrier)}
                    onChange={() =>
                      setFilterCarriers(toggleInArray(filterCarriers, carrier))
                    }
                  />
                );
              })}
            </Form.Group>

            {/* Départs */}
            <Form.Group className="mb-3">
              <Form.Label className="h5 fw-bold">
                Gares / Aéroports de départ
              </Form.Label>
              {uniqueDepartures.map((departLocation, i) => {
                const id = `departLoc-${i}`;
                return (
                  <Form.Check
                    key={id}
                    type="checkbox"
                    id={id}
                    label={departLocation}
                    checked={filterDepartures.includes(departLocation)}
                    onChange={() =>
                      setFilterDepartures(
                        toggleInArray(filterDepartures, departLocation)
                      )
                    }
                  />
                );
              })}
            </Form.Group>

            {/* Arrivées */}
            <Form.Group className="mb-3">
              <Form.Label className="h5 fw-bold">
                Gares / Aéroports d’arrivée
              </Form.Label>
              {uniqueArrivals.map((arrivalLocation, i) => {
                const id = `arrivalLoc-${i}`;
                return (
                  <Form.Check
                    key={id}
                    type="checkbox"
                    id={id}
                    label={arrivalLocation}
                    checked={filterArrivals.includes(arrivalLocation)}
                    onChange={() =>
                      setFilterArrivals(
                        toggleInArray(filterArrivals, arrivalLocation)
                      )
                    }
                  />
                );
              })}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFilterModal(false)}>
            Fermer
          </Button>
        </Modal.Footer>
      </Modal>

      <TransportDetailModal
        show={showDetailModal}
        onClose={() => closeSelectedOfferModal()}
        selectedOffer={selectedOffer}
        selectedAgentParams={selectedAgentParams}
      />
    </div>
  );
};

export default TransportSearchResults;
